import { Alert, Box, Menu, MenuItem, Typography } from '@mui/material';
import { MouseEvent, useMemo, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import NoTableDataImageBO from '../../../assets/image/empty-state-establecimientos-backoffice.png';
import BackdropComponent from '../../../components/Backdrop/BackdropComponent';
import DataTable from '../../../components/DataTable/DataTable';
import useUserAccess from '../../../hooks/useUserAccess';
import { useRoles } from '../../../hooks/useRoles';
import useRoutePrefix from '../../../hooks/useRoutePrefix';
import useTranslate from '../../../hooks/useTranslate';
import { getAccessValues } from '../../../components/layout/RoutesManager/access';
import DrawerManager, {
    DRAWER_ACTION_VALUES,
    type DrawerAction
} from '../../Visitas/components/drawers/DrawerManager';
import { useGetSiniestroVisitasQuery } from '../../../services/roamApi';
import {
    useCancelVisitMutation,
    useDownloadVisitaActaMutation,
    usePresentVisitMutation
} from '../../../services/visitasApi';
import { getDate } from '../../../utils/common';
import useAlerts from '../../Visitas/hooks/useAlerts';
import { VisitaEstado } from '../../Visitas/consts/visitasStatus';
import { VisitaEstadoChip } from '../../Visitas/components/EstadoChip';

interface SiniestroVisitsTabProps {
    siniestroId?: number;
}

const ASSIGNATION_VALID_STATES = [VisitaEstado.SIN_ASIGNAR];

const REASSIGNATION_VALID_STATES = [
    VisitaEstado.RECHAZADO,
    VisitaEstado.PENDIENTE,
    VisitaEstado.PROGRAMADA,
    VisitaEstado.EN_PROCESO
];

const CANCELABLE_VISIT_STATES = [
    VisitaEstado.SIN_ASIGNAR,
    VisitaEstado.RECHAZADO,
    VisitaEstado.PENDIENTE,
    VisitaEstado.PROGRAMADA,
    VisitaEstado.EN_PROCESO
];

const VISIT_DETAIL_VISIBLE_STATES = [2, 3, 4, 5, 7, 9, 10, 11];
const ACTA_DOWNLOAD_ALLOWED_STATES = [VisitaEstado.PRESENTADO, VisitaEstado.REALIZADA];

const getCellValue = (value: unknown) => {
    if (value === undefined || value === null || value === '') {
        return '-';
    }

    return String(value);
};

const getVisitType = (visita: any) => {
    if (Array.isArray(visita?.actividades) && visita.actividades.length > 0) {
        const descriptions = visita.actividades
            .map((actividad: any) => actividad?.tipoVisita?.descripcion)
            .filter(Boolean);

        if (descriptions.length) {
            return descriptions.join(', ');
        }
    }

    return visita?.tipoVisita?.descripcion || visita?.nombreTipoVisita || visita?.tipoVisita;
};

const getVisitTypeId = (visita: any) => {
    if (visita?.idTipoVisita) return Number(visita.idTipoVisita);
    if (visita?.tipoVisita?.id) return Number(visita.tipoVisita.id);
    if (Array.isArray(visita?.actividades) && visita.actividades.length > 0) {
        return Number(visita.actividades[0]?.tipoVisita?.id) || undefined;
    }
    return undefined;
};

const mapVisitForVisitModule = (visita: any) => ({
    ...visita,
    estado: Number(visita?.estado || visita?.idEstado) || undefined,
    idEstablecimiento: visita?.idEstablecimiento || visita?.establecimiento?.id,
    zipCode:
        visita?.zipCode ||
        visita?.establecimiento?.direccion?.localidad?.codigoPostal ||
        visita?.establecimiento?.direccion?.codigoPostal ||
        '',
    nombreTipoVisita: getVisitType(visita) || '-'
});

function SiniestroVisitsTab({ siniestroId }: SiniestroVisitsTabProps) {
    const { t } = useTranslation('siniestrosRoam');
    const { t: tVisitas } = useTranslation('visitas');
    const { translate } = useTranslate(tVisitas);
    const { showPresentVisitConfirmationAlert, showCancelVisitConfirmationAlert } = useAlerts({
        translate
    });
    const navigate = useNavigate();
    const access = useUserAccess();
    const FEATURES = getAccessValues(access);
    const { isAdminBO, isSupervisorSIMP } = useRoles();
    const routePrefix = useRoutePrefix();
    const {
        data: visitas,
        isFetching,
        isError,
        refetch
    } = useGetSiniestroVisitasQuery(siniestroId as number, {
        skip: !siniestroId
    });
    const [downloadVisitaActa] = useDownloadVisitaActaMutation();
    const [presentVisit, { isLoading: isApproving }] = usePresentVisitMutation();
    const [cancelVisit, { isLoading: isCancellingVisit }] = useCancelVisitMutation();
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
    const [selectedVisit, setSelectedVisit] = useState<any>(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerAction, setDrawerAction] = useState<DrawerAction | null>(null);

    const closeDrawer = () => {
        setDrawerOpen(false);
        setDrawerAction(null);
        setSelectedVisit(null);
    };

    const clearVisitsSelection = () => undefined;

    const handleOpenMenu = (event: MouseEvent<HTMLElement>, visita: any) => {
        setMenuAnchorEl(event.currentTarget);
        setSelectedVisit(mapVisitForVisitModule(visita));
    };

    const handleCloseMenu = () => {
        setMenuAnchorEl(null);
    };

    const handleOpenDrawer = (action: DrawerAction) => {
        handleCloseMenu();
        setDrawerAction(action);
        setDrawerOpen(true);
    };

    const handleOpenCancelVisitDialog = () => {
        handleCloseMenu();

        if (!selectedVisit?.id) return;

        showCancelVisitConfirmationAlert({
            confirmAction: async (observacion: string) => {
                try {
                    await cancelVisit({ visitId: Number(selectedVisit.id), observacion }).unwrap();

                    enqueueSnackbar(tVisitas('drawer.successCancel'), {
                        variant: 'success'
                    });

                    refetch();
                } catch (error: any) {
                    enqueueSnackbar(error?.data?.message || tVisitas('drawer.errorUpdate'), {
                        variant: 'error'
                    });
                }
            }
        });
    };

    const handleOpenVisitDetail = () => {
        if (!selectedVisit) return;

        handleCloseMenu();
        navigate(`/${routePrefix}/visitas/ejecucion`, {
            state: {
                selectedVisita: selectedVisit,
                siniestro: selectedVisit?.siniestro || { id: siniestroId }
            }
        });
    };

    const handleApproveVisit = async () => {
        if (!selectedVisit?.id) return;

        const visitId = Number(selectedVisit.id);

        showPresentVisitConfirmationAlert({
            confirmAction: async () => {
                try {
                    await presentVisit({ visitId }).unwrap();
                    enqueueSnackbar('Estado editado con Éxito.', { variant: 'success' });
                    handleCloseMenu();
                    refetch();
                } catch (error: any) {
                    enqueueSnackbar(
                        error?.data?.message || 'Error al intentar presentar la visita',
                        {
                            variant: 'error'
                        }
                    );
                }
            }
        });
    };

    const handleDownloadActa = async () => {
        if (!selectedVisit?.id) return;

        const idTipoVisita = getVisitTypeId(selectedVisit);

        if (!idTipoVisita) {
            enqueueSnackbar(tVisitas('alerts.downloadActa.error'), { variant: 'error' });
            return;
        }

        try {
            const response: any = await downloadVisitaActa({
                idVisita: Number(selectedVisit.id),
                idTipoVisita,
                tipoActa: 1
            }).unwrap();

            if (!response?.data) {
                enqueueSnackbar(tVisitas('alerts.downloadActa.error'), { variant: 'error' });
                return;
            }

            const byteCharacters = atob(response.data);
            const byteNumbers = Array.from(byteCharacters, (char) => char.charCodeAt(0));
            const blob = new Blob([new Uint8Array(byteNumbers)], {
                type: 'application/pdf'
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${tVisitas('table.actions.downloadActaFilename')}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            handleCloseMenu();
            enqueueSnackbar(tVisitas('alerts.downloadActa.success'), { variant: 'success' });
        } catch (error: any) {
            enqueueSnackbar(error?.data?.message || tVisitas('alerts.downloadActa.error'), {
                variant: 'error'
            });
        }
    };

    const selectedVisitState =
        Number(selectedVisit?.estado || selectedVisit?.idEstado) || undefined;
    const canManageVisit = Boolean(isAdminBO && FEATURES.GESTION_DE_VISITAS.hasAccess);
    // El supervisor solo consulta las visitas Presentadas: ver la ejecución y bajar el acta (SMP-460)
    const supervisorConsultaVisita = Boolean(
        isSupervisorSIMP && selectedVisitState === VisitaEstado.PRESENTADO
    );
    const canViewVisit = Boolean(
        (isAdminBO &&
            FEATURES.ALTA_DE_VISITAS.hasAccess &&
            selectedVisitState &&
            VISIT_DETAIL_VISIBLE_STATES.includes(selectedVisitState)) ||
            supervisorConsultaVisita
    );
    const canCancelVisit = Boolean(
        canManageVisit && selectedVisitState && CANCELABLE_VISIT_STATES.includes(selectedVisitState)
    );
    const canAssignPreventor = Boolean(
        canManageVisit &&
        selectedVisitState &&
        ASSIGNATION_VALID_STATES.includes(selectedVisitState)
    );
    const canReassignPreventor = Boolean(
        canManageVisit &&
        selectedVisitState &&
        REASSIGNATION_VALID_STATES.includes(selectedVisitState)
    );
    const canApproveVisit = Boolean(
        canManageVisit && selectedVisitState === VisitaEstado.REALIZADA && !isApproving
    );
    const canRequestChanges = Boolean(
        canManageVisit && selectedVisitState === VisitaEstado.REALIZADA
    );
    const canDownloadActa = Boolean(
        (canManageVisit || supervisorConsultaVisita) &&
        selectedVisitState &&
        ACTA_DOWNLOAD_ALLOWED_STATES.includes(selectedVisitState) &&
        selectedVisit?.id &&
        getVisitTypeId(selectedVisit)
    );

    const hasActions = useMemo(
        () =>
            canCancelVisit ||
            canAssignPreventor ||
            canReassignPreventor ||
            canViewVisit ||
            canApproveVisit ||
            canRequestChanges ||
            canDownloadActa,
        [
            canApproveVisit,
            canAssignPreventor,
            canCancelVisit,
            canDownloadActa,
            canReassignPreventor,
            canRequestChanges,
            canViewVisit
        ]
    );

    if (!siniestroId) {
        return (
            <Typography variant="body1">{t('detail.placeholdersTabs.visitsPending')}</Typography>
        );
    }

    if (isError) {
        return (
            <Alert severity="error" sx={{ mt: 2 }}>
                {t('detail.visits.error')}
            </Alert>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            <DataTable
                filterLocal
                noPaginated
                filters={{ skip: 0, take: 10 }}
                setFilters={() => undefined}
                isLoading={isFetching}
                items={visitas || []}
                menuOpen={Boolean(menuAnchorEl)}
                selectedItemFromAction={selectedVisit}
                noInfoImage={NoTableDataImageBO}
                noInfoTitle={t('detail.visits.emptyTitle')}
                noInfoSubtitle={t('detail.visits.emptySubtitle')}
                columnTitles={[
                    t('detail.visits.columns.id'),
                    t('detail.visits.columns.preventor'),
                    t('detail.visits.columns.status'),
                    t('detail.visits.columns.visitType'),
                    t('detail.visits.columns.deadline'),
                    tVisitas('table.columnsTitles.actions')
                ]}
                headers={[
                    {
                        type: 'data',
                        upperLabel: (visita: any) => getCellValue(visita.id)
                    },
                    {
                        type: 'data',
                        upperLabel: (visita: any) =>
                            getCellValue(
                                visita.preventor
                                    ? `${visita.preventor.nombre || ''} ${visita.preventor.apellido || ''}`.trim()
                                    : null
                            )
                    },
                    {
                        type: 'data',
                        upperLabel: (visita: any) => (
                            <VisitaEstadoChip
                                estadoId={visita.idEstado}
                                observacion={visita.observacion}
                                motivoFalla={visita.motivoFalla}
                            />
                        )
                    },
                    {
                        type: 'data',
                        upperLabel: (visita: any) => getCellValue(getVisitType(visita))
                    },
                    {
                        type: 'data',
                        upperLabel: (visita: any) =>
                            visita.fechaLimite ? getDate(visita.fechaLimite) : '-'
                    },
                    {
                        type: 'action',
                        centerColumn: true,
                        onClick: (event: MouseEvent<HTMLElement>, visita: any) => {
                            const visitState =
                                Number(visita?.estado || visita?.idEstado) || undefined;
                            const rowCanManageVisit = Boolean(
                                isAdminBO && FEATURES.GESTION_DE_VISITAS.hasAccess
                            );
                            const rowHasActions =
                                (rowCanManageVisit &&
                                    visitState &&
                                    (CANCELABLE_VISIT_STATES.includes(visitState) ||
                                        ASSIGNATION_VALID_STATES.includes(visitState) ||
                                        REASSIGNATION_VALID_STATES.includes(visitState) ||
                                        visitState === VisitaEstado.REALIZADA ||
                                        ACTA_DOWNLOAD_ALLOWED_STATES.includes(visitState))) ||
                                (isAdminBO &&
                                    FEATURES.ALTA_DE_VISITAS.hasAccess &&
                                    visitState &&
                                    VISIT_DETAIL_VISIBLE_STATES.includes(visitState)) ||
                                (isSupervisorSIMP && visitState === VisitaEstado.PRESENTADO);

                            if (!rowHasActions) return;

                            handleOpenMenu(event, visita);
                        }
                    }
                ]}
            />

            <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleCloseMenu}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
            >
                {selectedVisit && hasActions && (
                    <>
                        {canCancelVisit && (
                            <MenuItem
                                onClick={handleOpenCancelVisitDialog}
                                disabled={isCancellingVisit}
                            >
                                {tVisitas('drawer.cancelVisit')}
                            </MenuItem>
                        )}
                        {canAssignPreventor && (
                            <MenuItem
                                onClick={() =>
                                    handleOpenDrawer(DRAWER_ACTION_VALUES.ASSIGN_PREVENTOR)
                                }
                            >
                                {tVisitas('table.actions.assignPreventor')}
                            </MenuItem>
                        )}
                        {canReassignPreventor && (
                            <MenuItem
                                onClick={() =>
                                    handleOpenDrawer(DRAWER_ACTION_VALUES.REASSIGN_PREVENTOR)
                                }
                            >
                                {tVisitas('table.actions.reassignPreventor')}
                            </MenuItem>
                        )}
                        {canViewVisit && (
                            <MenuItem onClick={handleOpenVisitDetail}>
                                {tVisitas('table.actions.seeExecution')}
                            </MenuItem>
                        )}
                        {canApproveVisit && (
                            <MenuItem onClick={handleApproveVisit}>
                                {tVisitas('table.actions.presentVisit')}
                            </MenuItem>
                        )}
                        {canRequestChanges && (
                            <MenuItem
                                onClick={() =>
                                    handleOpenDrawer(DRAWER_ACTION_VALUES.SEND_VISIT_TO_REVISION)
                                }
                            >
                                {tVisitas('table.actions.sendToRevision')}
                            </MenuItem>
                        )}
                        {canDownloadActa && (
                            <MenuItem onClick={handleDownloadActa}>
                                {tVisitas('table.actions.downloadActa')}
                            </MenuItem>
                        )}
                    </>
                )}
            </Menu>

            <DrawerManager
                open={drawerOpen}
                actionType={drawerAction}
                setOpen={(value) => {
                    setDrawerOpen(value);
                    if (!value) {
                        closeDrawer();
                    }
                }}
                closeDrawer={closeDrawer}
                onSuccess={() => refetch()}
                selected={selectedVisit}
                translate={tVisitas}
                selectedVisitsIds={selectedVisit?.id ? [Number(selectedVisit.id)] : []}
                clearVisitsSelection={clearVisitsSelection}
            />

            <BackdropComponent loading={isCancellingVisit} />
        </Box>
    );
}

export default SiniestroVisitsTab;
